import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

let ffmpegInstance: FFmpeg | null = null;
let ffmpegLoading: Promise<FFmpeg> | null = null;

function isMp4Blob(blob: Blob): boolean {
  return /mp4|m4v|quicktime/i.test(blob.type);
}

async function getFfmpeg(): Promise<FFmpeg> {
  if (ffmpegInstance?.loaded) {
    return ffmpegInstance;
  }
  if (ffmpegLoading) {
    return ffmpegLoading;
  }

  ffmpegLoading = (async () => {
    const ffmpeg = new FFmpeg();
    const base = `${window.location.origin}/assets/ffmpeg`;
    await ffmpeg.load({
      coreURL: await toBlobURL(`${base}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${base}/ffmpeg-core.wasm`, 'application/wasm'),
    });
    ffmpegInstance = ffmpeg;
    return ffmpeg;
  })();

  try {
    return await ffmpegLoading;
  } catch (error) {
    ffmpegLoading = null;
    throw error;
  }
}

/**
 * Ensure a recorded blob is seekable MP4 (H.264 + faststart).
 * Chrome MediaRecorder usually yields WebM; Safari may already give MP4.
 */
export async function ensureMp4Blob(input: Blob): Promise<Blob> {
  if (isMp4Blob(input)) {
    return input.type === 'video/mp4' ? input : new Blob([input], { type: 'video/mp4' });
  }

  const ffmpeg = await getFfmpeg();
  const inputName = 'input.webm';
  const outputName = 'output.mp4';

  await ffmpeg.writeFile(inputName, await fetchFile(input));
  const code = await ffmpeg.exec([
    '-i', inputName,
    '-c:v', 'libx264',
    '-pix_fmt', 'yuv420p',
    '-movflags', '+faststart',
    '-an',
    outputName,
  ]);

  if (code !== 0) {
    await safeDelete(ffmpeg, inputName);
    throw new Error(`ffmpeg exited with code ${code}`);
  }

  const data = await ffmpeg.readFile(outputName);
  await safeDelete(ffmpeg, inputName);
  await safeDelete(ffmpeg, outputName);

  const bytes = data instanceof Uint8Array ? data : new TextEncoder().encode(String(data));
  return new Blob([bytes], { type: 'video/mp4' });
}

async function safeDelete(ffmpeg: FFmpeg, name: string) {
  try {
    await ffmpeg.deleteFile(name);
  } catch {
    // ignore cleanup failures
  }
}

export function pickRecorderMimeType(): string {
  const candidates = [
    'video/mp4;codecs=avc1.42E01E',
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];
  return candidates.find(type => MediaRecorder.isTypeSupported(type)) ?? '';
}
